import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';
import { Shield, Plus, Users, DollarSign, Clock, TrendingUp, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';

// ==================== Mock Data ====================

interface SMSFFund {
  id: string;
  name: string;
  abn: string;
  totalAssets: number;
  members: number;
  pensionMembers: number;
  auditStatus: 'completed' | 'in-progress' | 'not-started';
  lodgementStatus: 'lodged' | 'overdue' | 'draft' | 'not-started';
  complianceStatus: 'green' | 'amber' | 'red';
  nextLodgementDue: string;
}

const MOCK_FUNDS: SMSFFund[] = [
  {
    id: 'fund-001',
    name: 'Smith Family Super Fund',
    abn: '12 345 678 901',
    totalAssets: 1_850_000,
    members: 2,
    pensionMembers: 1,
    auditStatus: 'completed',
    lodgementStatus: 'lodged',
    complianceStatus: 'green',
    nextLodgementDue: '28/02/2027',
  },
  {
    id: 'fund-002',
    name: 'Johnson Retirement Fund',
    abn: '98 765 432 109',
    totalAssets: 2_340_000,
    members: 3,
    pensionMembers: 2,
    auditStatus: 'in-progress',
    lodgementStatus: 'draft',
    complianceStatus: 'amber',
    nextLodgementDue: '28/02/2027',
  },
  {
    id: 'fund-003',
    name: 'ABC Pty Ltd Superannuation Fund',
    abn: '11 222 333 444',
    totalAssets: 520_000,
    members: 4,
    pensionMembers: 0,
    auditStatus: 'not-started',
    lodgementStatus: 'overdue',
    complianceStatus: 'red',
    nextLodgementDue: '28/02/2026',
  },
  {
    id: 'fund-004',
    name: 'Garcia & Partners Super Fund',
    abn: '55 666 777 888',
    totalAssets: 980_000,
    members: 2,
    pensionMembers: 1,
    auditStatus: 'completed',
    lodgementStatus: 'lodged',
    complianceStatus: 'green',
    nextLodgementDue: '28/02/2027',
  },
];

const AUDIT_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  completed: { label: 'Completed', variant: 'default' },
  'in-progress': { label: 'In Progress', variant: 'secondary' },
  'not-started': { label: 'Not Started', variant: 'outline' },
};

const LODGEMENT_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  lodged: { label: 'Lodged', variant: 'default' },
  overdue: { label: 'Overdue', variant: 'destructive' },
  draft: { label: 'Draft', variant: 'secondary' },
  'not-started': { label: 'Not Started', variant: 'outline' },
};

const COMPLIANCE_DOT: Record<string, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

// ==================== Component ====================

export default function SMSFDashboardPage() {
  const navigate = useNavigate();

  const { data: apiFunds, isLoading } = useQuery({
    queryKey: ['smsf-funds'],
    queryFn: async () => {
      const r = await apiClient.get('/smsf/funds');
      return r.data.data;
    },
  });

  // Map API funds to expected shape, fall back to mock data
  const funds: SMSFFund[] = (apiFunds && apiFunds.length > 0)
    ? (apiFunds as any[]).map((f: any) => ({
        id: f.id,
        name: f.fund_name || f.name,
        abn: f.abn || '',
        totalAssets: f.total_assets ?? f.totalAssets ?? 0,
        members: f.member_count ?? f.members ?? 0,
        pensionMembers: f.pension_members ?? f.pensionMembers ?? 0,
        auditStatus: f.audit_status ?? f.auditStatus ?? 'not-started',
        lodgementStatus: f.lodgement_status ?? f.lodgementStatus ?? 'not-started',
        complianceStatus: f.compliance_status ?? f.complianceStatus ?? 'green',
        nextLodgementDue: f.next_lodgement_due ?? f.nextLodgementDue ?? '',
      }))
    : MOCK_FUNDS;

  const totalAssets = funds.reduce((s, f) => s + f.totalAssets, 0);
  const totalMembers = funds.reduce((s, f) => s + f.members, 0);
  const pensionMembers = funds.reduce((s, f) => s + f.pensionMembers, 0);
  const upcomingLodgements = funds.filter(f => f.lodgementStatus !== 'lodged').length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Shield}
        title="SMSF Management"
        description="Self-Managed Super Fund overview and compliance tracking"
        actions={
          <Button onClick={() => navigate('/panel/smsf/new')} size="sm">
            <Plus className="h-4 w-4 mr-1.5" />
            Register New Fund
          </Button>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#5048e5]/10 text-[#5048e5] flex items-center justify-center">
                <Shield className="h-4.5 w-4.5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Funds</p>
                <p className="text-xl font-bold">{funds.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                <DollarSign className="h-4.5 w-4.5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Assets</p>
                <p className="text-xl font-bold">${totalAssets.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-blue-500/10 text-blue-600 flex items-center justify-center">
                <Users className="h-4.5 w-4.5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Members in Pension</p>
                <p className="text-xl font-bold">{pensionMembers} <span className="text-sm font-normal text-muted-foreground">/ {totalMembers}</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center">
                <Clock className="h-4.5 w-4.5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Upcoming Lodgements</p>
                <p className="text-xl font-bold">{upcomingLodgements}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading funds...</span>
        </div>
      )}

      {/* Funds Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All Funds</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Fund Name</th>
                  <th className="pb-2 pr-4 font-medium">ABN</th>
                  <th className="pb-2 pr-4 font-medium text-right">Total Assets</th>
                  <th className="pb-2 pr-4 font-medium text-center">Members</th>
                  <th className="pb-2 pr-4 font-medium text-center">Compliance</th>
                  <th className="pb-2 pr-4 font-medium">Audit Status</th>
                  <th className="pb-2 pr-4 font-medium">Lodgement</th>
                  <th className="pb-2 font-medium">Next Due</th>
                </tr>
              </thead>
              <tbody>
                {funds.map(fund => {
                  const audit = AUDIT_BADGE[fund.auditStatus];
                  const lodge = LODGEMENT_BADGE[fund.lodgementStatus];
                  return (
                    <tr
                      key={fund.id}
                      onClick={() => navigate(`/panel/smsf/${fund.id}`)}
                      className="border-b last:border-0 cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <td className="py-3 pr-4 font-medium">{fund.name}</td>
                      <td className="py-3 pr-4 text-muted-foreground font-mono text-xs">{fund.abn}</td>
                      <td className="py-3 pr-4 text-right font-medium">${fund.totalAssets.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-center">{fund.members}</td>
                      <td className="py-3 pr-4 text-center">
                        <span className={cn('inline-block h-3 w-3 rounded-full', COMPLIANCE_DOT[fund.complianceStatus])} />
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant={audit.variant}>{audit.label}</Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant={lodge.variant}>{lodge.label}</Badge>
                      </td>
                      <td className="py-3 text-muted-foreground text-xs">{fund.nextLodgementDue}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
